import { Tag, Tooltip } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import type { SessionQualification } from '@evimo/types/enums/session-qualification';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import type { SessionChargeResponseDto } from '@evimo/types/api/session/session.response.dto';
import { SessionIdentifierCell } from '~/app/components/session/ChargesTable/components/SessionIdentifierCell/SessionIdentifierCell';
import { useActivityWords } from '~/app/components/session/hooks/useActivityWords';
import { shouldShowAnomalyQualification } from '~/app/components/session/utils/qualificationDisplay';
import { TableRowActionsMenu } from '~/app/components/common/TableRowActionsMenu/TableRowActionsMenu';

function outcomeColor(outcome: string) {
  if (outcome === 'in_progress') return 'processing';
  return 'success';
}

export function useChargesColumns(): ColumnsType<SessionChargeResponseDto> {
  const words = useActivityWords();
  const navigate = useNavigate();
  const na = words.t('unknown');

  return [
    {
      title: words.t('columns.sessionId'),
      key: 'sessionIdentifier',
      minWidth: 180,
      render: (_, row) => {
        const identifier = row.identifier?.trim();
        if (!identifier) {
          return na;
        }
        return (
          <SessionIdentifierCell
            identifier={identifier}
            transactionId={row.transactionId}
          />
        );
      },
    },
    {
      title: words.t('columns.status'),
      key: 'outcome',
      minWidth: 140,
      render: (_, row) =>
        row.outcome ? (
          <Tag color={outcomeColor(row.outcome)}>
            {words.sessionStatusLabel(row)}
          </Tag>
        ) : (
          na
        ),
    },
    {
      title: words.t('columns.anomalyDetected'),
      key: 'anomaly',
      minWidth: 160,
      render: (_, row) => {
        const q = row.qualification as SessionQualification | null;
        if (q == null || !shouldShowAnomalyQualification(q)) {
          return na;
        }
        const tag = (
          <Tag color={words.qualificationColor(q)}>
            {words.qualificationLabel(q)}
          </Tag>
        );
        const hint = words.qualificationTooltip(q);
        return hint ? <Tooltip title={hint}>{tag}</Tooltip> : tag;
      },
    },
    {
      title: words.t('columns.charger'),
      dataIndex: 'chargerName',
      minWidth: 180,
      render: (value: string | null) =>
        value?.trim() ? words.name(value) : na,
    },
    {
      title: words.t('columns.started'),
      dataIndex: 'startedAt',
      minWidth: 165,
      render: (value: string | null) => (value ? words.clock(value) : na),
    },
    {
      title: words.t('columns.ended'),
      dataIndex: 'stoppedAt',
      minWidth: 165,
      render: (value: string | null) => (value ? words.clock(value) : na),
    },
    {
      title: words.t('columns.howLong'),
      key: 'duration',
      minWidth: 110,
      render: (_, row) => words.duration(row.startedAt, row.stoppedAt),
    },
    {
      title: words.t('columns.connector'),
      dataIndex: 'connectorName',
      minWidth: 130,
      render: (value: string | null) =>
        value?.trim() ? words.name(value) : na,
    },
    {
      title: words.t('columns.accessCard'),
      dataIndex: 'accessCard',
      minWidth: 160,
      render: (value: string | null) =>
        value?.trim() ? words.name(value) : na,
    },
    {
      title: words.t('columns.energy'),
      dataIndex: 'energyKwh',
      align: 'right',
      minWidth: 130,
      render: (value: number | null) => words.energy(value),
    },
    {
      key: 'actions',
      minWidth: 56,
      align: 'center',
      render: (_, row) => (
        <TableRowActionsMenu
          items={[
            {
              key: 'details',
              label: words.t('actions.viewDetails'),
              icon: <EyeOutlined />,
              onClick: () => navigate(`/sessions/${row.identifier}`),
            },
          ]}
        />
      ),
    },
  ];
}
